import React from "react";
import { FaFolderOpen, FaPlus, FaSearch, FaUsers } from "react-icons/fa";
import AttendanceMemberCard from "./AttendanceMemberCard";
import { cn, getGroupAccentClass } from "../utils/attendancePageUtils";

function SummaryCard({ label, value, className = "" }) {
  return (
    <div className={cn("rounded-2xl px-4 py-4", className)}>
      <p className="text-xs font-semibold uppercase tracking-wider opacity-80">
        {label}
      </p>
      <p className="mt-2 text-2xl font-black">{value}</p>
    </div>
  );
}

const AttendanceMembersSection = ({
  selectedDojo = "",
  selectedGroup = "",
  selectedGroupData = null,
  loadingMembers = false,
  searchTerm = "",
  onSearchChange,
  adherents = [],
  filteredAdherents = [],
  attendance = {},
  attendanceSummary = {
    total: 0,
    present: 0,
    absent: 0,
    sick: 0,
    pending: 0,
  },
  savingAttendanceMap = {},
  onOpenInfo,
  onUpdateAttendance,
  onOpenCreateAdherent,
  canCreateAdherent = false,
}) => {
  if (!selectedDojo) {
    return (
      <section className="mt-6 rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          <FaFolderOpen className="text-2xl" />
        </div>
        <h3 className="mt-4 text-xl font-black text-slate-900 dark:text-slate-100">
          Sélectionne d’abord un lieu
        </h3>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Choisis un lieu puis un groupe pour afficher l’appel.
        </p>
      </section>
    );
  }

  if (!selectedGroup) {
    return (
      <section className="mt-6 rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          <FaUsers className="text-2xl" />
        </div>
        <h3 className="mt-4 text-xl font-black text-slate-900 dark:text-slate-100">
          Sélectionne un groupe
        </h3>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Les adhérents et leurs statuts de présence s’afficheront ici.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-6 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
              Appel du groupe
            </h2>

            {selectedGroupData ? (
              <span
                className={cn(
                  "inline-flex rounded-full border px-3 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200",
                  getGroupAccentClass(selectedGroupData.color_groupe)
                )}
              >
                {selectedGroupData.nom_groupe}
              </span>
            ) : null}
          </div>

          {selectedGroupData?.description_groupe ? (
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              {selectedGroupData.description_groupe}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-[260px] max-w-full">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Rechercher un adhérent..."
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
            />
          </div>

          <button
            type="button"
            onClick={onOpenCreateAdherent}
            disabled={!canCreateAdherent}
            className={cn(
              "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 font-semibold text-white transition",
              canCreateAdherent
                ? "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
                : "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
            )}
          >
            <FaPlus />
            Ajouter un adhérent
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <SummaryCard
          label="Total"
          value={attendanceSummary.total}
          className="border border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-100"
        />
        <SummaryCard
          label="Présents"
          value={attendanceSummary.present}
          className="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
        />
        <SummaryCard
          label="Absents"
          value={attendanceSummary.absent}
          className="bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300"
        />
        <SummaryCard
          label="Malades / Blessés"
          value={attendanceSummary.sick}
          className="bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
        />
        <SummaryCard
          label="Non pointés"
          value={attendanceSummary.pending}
          className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
        />
      </div>

      <div className="mt-6">
        {loadingMembers ? (
          <div className="grid gap-4 xl:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="h-56 animate-pulse rounded-[24px] border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : adherents.length === 0 ? (
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-8 text-center dark:border-slate-800 dark:bg-slate-800/60">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm dark:bg-slate-900 dark:text-slate-300">
              <FaFolderOpen className="text-2xl" />
            </div>
            <h3 className="mt-4 text-xl font-black text-slate-900 dark:text-slate-100">
              Aucun adhérent dans ce groupe
            </h3>
          </div>
        ) : filteredAdherents.length === 0 ? (
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-8 text-center dark:border-slate-800 dark:bg-slate-800/60">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm dark:bg-slate-900 dark:text-slate-300">
              <FaSearch className="text-2xl" />
            </div>
            <h3 className="mt-4 text-xl font-black text-slate-900 dark:text-slate-100">
              Aucun résultat
            </h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Modifie ta recherche pour retrouver un adhérent.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {filteredAdherents.map((adherent) => (
              <AttendanceMemberCard
                key={adherent.id}
                adherent={adherent}
                attendanceStatus={attendance[adherent.id] || ""}
                saving={Boolean(savingAttendanceMap[adherent.id])}
                onOpenInfo={onOpenInfo}
                onUpdateAttendance={onUpdateAttendance}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AttendanceMembersSection;